import './confirmation-cid.scss'

import React, { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

import Header from '../../components/collections/Header'
import { CoverSpinning } from '../../components/elements'
import Modal from '../../components/modals/Modal'
import { NoticeMessage } from '../../constants'
import TagManager from '../../lib/tag-manager'
import { isValidCID } from '../../lib/utils'
import { ActionType } from '../../providers/action'
import { useAppState, useDispatcher } from '../../providers/store'
import getApi from '../../services'

interface FormStateCID {
    cid: string
}

interface SubmitResponse {
    showModal: boolean
    title: string
    message: string
    confirmation: boolean
}

interface NoticeCIDModalProps {
    title: string
    message: string
    confirmation: boolean
    onClose: () => void
    onAbort: () => void
    onConfirm: () => void
}

function NoticeCIDModal({
    title,
    message,
    confirmation,
    onClose,
    onAbort,
    onConfirm,
}: NoticeCIDModalProps) {
    return (
        <Modal>
            <div className="notice-modal-content abcs-container">
                <h4>{title}</h4>
                <p>{message}</p>
                {confirmation && (
                    <div className="confirmation-button-container">
                        <button
                            type="button"
                            className="abcs-button primary fluid"
                            onClick={onAbort}
                        >
                            ยกเลิก
                        </button>
                        <button
                            type="button"
                            className="abcs-button secondary basic fluid"
                            onClick={onConfirm}
                        >
                            ยืนยัน
                        </button>
                    </div>
                )}
                {!confirmation && (
                    <button
                        type="button"
                        className="abcs-button secondary"
                        onClick={onClose}
                        style={{ color: 'white' }}
                    >
                        ปิด
                    </button>
                )}
            </div>
        </Modal>
    )
}

export default function ConfirmationCID() {
    const navigate = useNavigate()
    const dispatch = useDispatcher()
    const appState = useAppState()
    const [loading, setLoading] = useState(false)
    const [noticeModal, setNoticeModal] = useState<SubmitResponse>({
        message: '',
        title: '',
        showModal: false,
        confirmation: false,
    })
    const context = new URLSearchParams(window.location.search).get('context') || 'repayment'
    const isKYCForCampaign = context === 'campaign'
    const { otpService } = getApi()
    const {
        register,
        handleSubmit,
        formState: { errors },
        trigger,
        reset,
        clearErrors,
        getValues,
    } = useForm<FormStateCID>()
    const cidField = register('cid', {
        required: {
            value: true,
            message: 'กรุณาระบุหมายเลขประจำตัวประชาชน',
        },
        validate: (value: string) => {
            if (process.env.DISABLED_REAL_CID_CHECKED === 'true') {
                return true
            }
            if (!isValidCID(value)) {
                return 'ข้อมูลไม่ถูกต้อง กรุณาตรวจสอบและดำเนินการอีกครั้ง'
            }
            return true
        },
        maxLength: 13,
    })

    useEffect(() => {
        function updateContext() {
            dispatch({
                type: ActionType.SAVED_KYC_CONTEXT_TYPE,
                payload: context,
            })
        }
        async function checkAlreadyKYC() {
            if (context === 'campaign') {
                try {
                    setLoading(true)
                    TagManager.popupEvent({
                        page: 'verification_cid',
                        field: 'camapign',
                        action: 'check_kyc_before',
                    })
                    const profile = await window.liff.getProfile()
                    const iskyc = await getApi().connectService.alreadyKycByUid(profile.userId)
                    if (iskyc) {
                        window.location.href = `https://liff.line.me/${process.env.SHARING_CAMPAIGN}`
                    }
                } catch {
                    TagManager.popupEvent({
                        page: 'verification_cid',
                        field: 'camapign',
                        action: 'check_kyc_failure',
                    })
                } finally {
                    setLoading(false)
                }
            }
        }
        if (!appState.kycContext) {
            updateContext()
            checkAlreadyKYC()
        }
    }, [appState.kycContext, context, dispatch])

    useMemo(() => {
        TagManager.loadEvent({ page: 'verification_cid' })
    }, [])

    const validate = async () => {
        await trigger()
    }

    const proceed = (result, data) => {
        if (result) {
            dispatch({ type: ActionType.SAVED_REFCODE, payload: { ...result, cid: data.cid } })
        }
        setLoading(false)
        navigate('/kyc/verification-otp')
    }

    const onSubmitCID =
        (force = false) =>
        async (data: FormStateCID) => {
            setLoading(true)
            try {
                const { userId } = await window.liff.getProfile()
                const result = await otpService.requestOTPByCID(data.cid, userId, force)
                proceed(result, data)
            } catch (error) {
                setLoading(false)
                if (error.response && error.response.data) {
                    const message =
                        NoticeMessage[error.response.data.code] || NoticeMessage['not.in.kyc']
                    const title =
                        error.response.data.code === 'already.kyc'
                            ? 'แจ้งเตือน'
                            : 'ข้อมูลไม่ถูกต้อง'
                    const confirmation = error.response.data.code === 'already.kyc'
                    setNoticeModal({ showModal: true, message, title, confirmation })

                    const field =
                        error.response.data.code === 'already.kyc' ? 'already KYC' : 'incorrect_cid'
                    TagManager.popupEvent({
                        page: 'verification_cid',
                        field,
                    })
                }
                reset({ cid: undefined })
            }
        }

    const closeNoticeModal = () => {
        setNoticeModal((prev) => ({ ...prev, showModal: false }))
        clearErrors()
        reset({ cid: undefined })
    }

    const abortNoticeModal = async () => {
        closeNoticeModal()
        if (window.liff.isInClient()) {
            await window.liff.sendMessages([
                {
                    type: 'text',
                    text: NoticeMessage.ABORTED_AUTHEN,
                },
            ])
            window.liff.closeWindow()
        }
    }

    const confirmNoticeModal = async () => {
        const data = getValues()
        setNoticeModal((prev) => ({ ...prev, showModal: false }))
        setLoading(true)
        await onSubmitCID(true)(data)
    }

    return (
        <>
            {loading && <CoverSpinning />}
            {noticeModal.showModal && (
                <NoticeCIDModal
                    confirmation={noticeModal.confirmation}
                    message={noticeModal.message}
                    title={noticeModal.title}
                    onClose={closeNoticeModal}
                    onAbort={abortNoticeModal}
                    onConfirm={confirmNoticeModal}
                />
            )}
            <div className="confirmation-cid">
                <Header
                    title="ยืนยันตัวตน"
                    messages={
                        isKYCForCampaign
                            ? []
                            : [
                                  {
                                      type: 'text',
                                      text: NoticeMessage.ABORTED_AUTHEN_LINE_SHARING,
                                  },
                              ]
                    }
                />
                <div className="confirmation-container abcs-container">
                    <div className="description-connect">
                        <span>กรุณากรอกข้อมูลของท่าน</span>
                        <p>
                            การเชื่อมต่อระหว่าง LINE กับ แอป Money Thunder
                            จะดำเนินการเพียงครั้งเดียวเท่านั้น ไม่สามารถเปลี่ยนเป็นผู้ใช้อื่นได้
                        </p>
                    </div>

                    <div className={`abcs-input ${errors.cid && 'error'}`}>
                        <label htmlFor="cid">หมายเลขบัตรประชาชน</label>
                        <input
                            id="cid"
                            maxLength={13}
                            pattern="[0-9]*"
                            type="text"
                            inputMode="numeric"
                            name={cidField.name}
                            onChange={cidField.onChange}
                            ref={cidField.ref}
                            onBlur={(e) => {
                                cidField.onBlur(e)
                                validate()
                            }}
                        />
                        {errors.cid && <small>{errors.cid.message}</small>}
                    </div>
                    <footer className="footer">
                        <button
                            className={`abcs-button primary  fluid ${errors.cid && 'disabled'}`}
                            type="submit"
                            onClick={handleSubmit((data) => onSubmitCID(false)(data))}
                        >
                            ต่อไป
                        </button>
                    </footer>
                </div>
            </div>
        </>
    )
}
